import React, { useState } from 'react';
import { Button, Drawer, Input, Switch, useToast } from '../../ui/components';
import { useT } from '../../i18n';
import { usersApi } from './usersApi';
import RoleAssignmentsEditor, { assignmentsFromRows, rowsFromAssignments, validateRows } from './RoleAssignmentsEditor';

export default function UserEditorDrawer({ open, user, roles, properties, isSelf, onClose, onSaved }) {
  const toast = useToast();
  const t = useT();
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [isActive, setIsActive] = useState(user?.isActive ?? true);
  const [assignments, setAssignments] = useState(() => rowsFromAssignments(user?.roleAssignments));
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const invalid = validateRows(assignments);
    if (invalid) { toast.error(t(invalid)); return; }
    setSaving(true);
    try {
      const updated = await usersApi.updateUser(user.id, {
        id: user.id,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        isActive,
        roleAssignments: assignmentsFromRows(assignments),
      });
      toast.success(t('User updated'));
      onSaved?.(updated);
    } catch (err) {
      toast.error(t('Could not update user'), { description: err?.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer open={open} onClose={() => (saving ? null : onClose())} title={t('Edit user')} size="md">
      <form className="set-form" onSubmit={submit}>
        <div className="usr-editor__email">{user?.email}</div>
        <div className="usr-editor__grid2">
          <Input label={t('First name')} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          <Input label={t('Last name')} value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>

        <Switch
          checked={isActive}
          onChange={setIsActive}
          label={t('Active')}
          disabled={isSelf}
        />
        {isSelf && <p className="set-section__hint">{t('You cannot deactivate your own account.')}</p>}

        <div>
          <span className="sp-field-label">{t('Roles & access')}</span>
          <RoleAssignmentsEditor
            value={assignments}
            onChange={setAssignments}
            roles={roles}
            properties={properties}
          />
        </div>

        <div className="set-form__actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>{t('Cancel')}</Button>
          <Button type="submit" variant="primary" loading={saving}>{t('Save changes')}</Button>
        </div>
      </form>
    </Drawer>
  );
}
